import { get, set, del, keys } from 'idb-keyval';

/**
 * Local safety net for an inspection in progress.
 *
 * A fitter marks 73 items stood next to a vehicle, often on a yard with no
 * signal. Every mark is written to IndexedDB as well as being pushed to the
 * server, so closing the app, losing signal or a flat battery costs nothing.
 * Pending server writes are replayed when the connection comes back.
 */

const DRAFT = (id: string) => `fc.draft.${id}`;
const QUEUE = (id: string) => `fc.queue.${id}`;

export interface DraftMarks {
  [itemKey: string]: { mc: string; note: string | null };
}

export interface StoredDraft {
  marks: DraftMarks;
  /** Fields outside the item list: dates, measurements, comments. */
  fields: Record<string, unknown>;
  savedAt: number;
}

export async function saveDraft(id: string, marks: DraftMarks, fields: Record<string, unknown>) {
  try {
    await set(DRAFT(id), { marks, fields, savedAt: Date.now() } satisfies StoredDraft);
  } catch {
    /* storage unavailable - the server copy is still the record */
  }
}

export async function loadDraft(id: string): Promise<StoredDraft | null> {
  try {
    return (await get<StoredDraft>(DRAFT(id))) ?? null;
  } catch {
    return null;
  }
}

export async function clearDraft(id: string) {
  try {
    await del(DRAFT(id));
    await del(QUEUE(id));
  } catch {
    /* nothing to clean up */
  }
}

/**
 * Patches that failed to reach the server. Merged rather than appended so a
 * long offline spell does not build an unbounded replay log - only the latest
 * value of each field matters.
 */
export async function queuePatch(id: string, patch: Record<string, unknown>) {
  try {
    const existing = (await get<Record<string, unknown>>(QUEUE(id))) ?? {};
    const merged: Record<string, unknown> = { ...existing, ...patch };
    if (patch.items && existing.items) {
      merged.items = { ...(existing.items as object), ...(patch.items as object) };
    }
    await set(QUEUE(id), merged);
  } catch {
    /* nothing more we can do; the local draft still holds the marks */
  }
}

export async function takeQueued(id: string): Promise<Record<string, unknown> | null> {
  try {
    const queued = await get<Record<string, unknown>>(QUEUE(id));
    if (!queued || Object.keys(queued).length === 0) return null;
    return queued;
  } catch {
    return null;
  }
}

export async function clearQueue(id: string) {
  try {
    await del(QUEUE(id));
  } catch {
    /* ignore */
  }
}

/** Inspection ids that still have unsent work, for a "not synced" warning. */
export async function pendingInspectionIds(): Promise<string[]> {
  try {
    const all = await keys();
    return all
      .filter((k): k is string => typeof k === 'string' && k.startsWith('fc.queue.'))
      .map((k) => k.slice('fc.queue.'.length));
  } catch {
    return [];
  }
}

/** Drops local copies of inspections that are finished and locked server-side. */
export async function pruneDrafts(lockedIds: string[]) {
  await Promise.all(lockedIds.map((id) => clearDraft(id)));
}
